/**
 * Site (structure) scope routes — the pages that mount inside
 * SiteDashboardLayout for a single structure: the dashboard, device
 * configuration, media/images, proof-of-performance, status/maintenance/
 * alarm history, calendar, compliance, activity + command logs, the power
 * readings family, solar options, and per-site tickets. Also carries the
 * standalone plant-level compliance dashboard, kept separate from the
 * structure-scoped compliance child route.
 *
 * Contains:
 * - siteRoutes — a fragment of <Route> elements for the composer
 */
import React from 'react';
import { Navigate, Route } from 'react-router-dom';

import {
  determineCanConfigureDeviceRoute,
  determineIsStaff,
  determineSpecificPlantAccess,
  determineSpecificStructureAccess,
} from '@sophocles/router/checkCredentials';
import { siteGroupCompliancePath, sitePath } from '@sophocles/router/paths';
import RequireAuth from '@sophocles/router/RequireAuth';
import SiteComplianceView from '@sophocles/views/reports/SiteComplianceView';
import SiteGroupDashboardView from '@sophocles/views/reports/SiteGroupDashboardView';
import SiteSettingsPage from '@sophocles/views/settings/SiteSettingsViewV2';
import ActivityLogPage from '@sophocles/views/sites/ActivityLogView';
import AlarmHistoryView from '@sophocles/views/sites/AlarmHistoryView';
import AnsiDoubleConsumptionView from '@sophocles/views/sites/AnsiDoubleConsumptionView';
import CalendarView from '@sophocles/views/sites/CalendarView';
import ChartingView from '@sophocles/views/sites/ChartingView';
import CicStatusView from '@sophocles/views/sites/CicStatusView';
import CommandLogPage from '@sophocles/views/sites/CommandLogView';
import DeviceConfigurationView from '@sophocles/views/sites/DeviceConfigurationView';
import DeviceImagesView from '@sophocles/views/sites/DeviceImagesView';
import GenericStatusView from '@sophocles/views/sites/GenericStatusView';
import InternationalMeterReadingsView from '@sophocles/views/sites/InternationalMeterReadingsView';
import MaintenanceHistoryView from '@sophocles/views/sites/MaintenanceHistoryView';
import MediaView from '@sophocles/views/sites/MediaView';
import PowerConsumptionView from '@sophocles/views/sites/PowerConsumptionView';
import ReadingsRouterView from '@sophocles/views/sites/ReadingsRouterView';
import SiteDashboardLayout from '@sophocles/views/sites/SiteDashboardLayout';
import SiteDashboardPopView from '@sophocles/views/sites/SiteDashboardPopView';
import SiteDashboardView from '@sophocles/views/sites/SiteDashboardView';
import SolarOptionsView from '@sophocles/views/sites/SolarOptionsView';
import SolarPowerView from '@sophocles/views/sites/SolarPowerView';
import StatusHistoryView from '@sophocles/views/sites/StatusHistoryView';
import { NewTicketViewSite } from '@sophocles/views/tickets/NewTicketView';
import SiteTicketGridView from '@sophocles/views/tickets/SiteTicketGridView';

export const siteRoutes = (
  <>
    <Route
      path={sitePath.pattern}
      element={<RequireAuth component={SiteDashboardLayout} onEnter={determineSpecificStructureAccess} />}
    >
      <Route index element={<SiteDashboardView />} />
      <Route
        path="configuration"
        element={<RequireAuth component={DeviceConfigurationView} onEnter={determineCanConfigureDeviceRoute} />}
      />
      <Route path="media" element={<RequireAuth component={MediaView} onEnter={determineSpecificStructureAccess} />} />
      <Route
        path="deviceimages"
        element={<RequireAuth component={DeviceImagesView} onEnter={determineSpecificStructureAccess} />}
      />
      <Route
        path="pop"
        element={<RequireAuth component={SiteDashboardPopView} onEnter={determineSpecificStructureAccess} />}
      />
      <Route path="statushist" element={<RequireAuth component={StatusHistoryView} onEnter={determineIsStaff} />} />
      <Route
        path="calendar"
        element={<RequireAuth component={CalendarView} onEnter={determineSpecificPlantAccess} />}
      />
      <Route
        path="settings"
        element={<RequireAuth component={SiteSettingsPage} onEnter={determineSpecificStructureAccess} />}
      />
      <Route
        path="settings/:section"
        element={<RequireAuth component={SiteSettingsPage} onEnter={determineSpecificStructureAccess} />}
      />
      <Route
        path="maintenancehist"
        element={<RequireAuth component={MaintenanceHistoryView} onEnter={determineSpecificStructureAccess} />}
      />
      <Route
        path="alarmhist"
        element={<RequireAuth component={AlarmHistoryView} onEnter={determineSpecificStructureAccess} />}
      />
      <Route
        path="compliance"
        element={<RequireAuth component={SiteComplianceView} onEnter={determineSpecificStructureAccess} />}
      />
      <Route
        path="activity-log"
        element={<RequireAuth component={ActivityLogPage} onEnter={determineSpecificStructureAccess} />}
      />
      <Route
        path="command-log"
        element={<RequireAuth component={CommandLogPage} onEnter={determineSpecificStructureAccess} />}
      />
      <Route path="statuses" element={<RequireAuth component={GenericStatusView} onEnter={determineIsStaff} />} />
      {/* Legacy params/thresholds/schedulecommands pages consolidated into
          the unified configuration view. */}
      <Route path="params" element={<Navigate to="../configuration" replace />} />
      <Route path="thresholds" element={<Navigate to="../configuration" replace />} />
      <Route path="schedulecommands" element={<Navigate to="../configuration" replace />} />
      <Route path="cicstatushist" element={<RequireAuth component={CicStatusView} onEnter={determineIsStaff} />} />
      <Route
        path="charting"
        element={<RequireAuth component={ChartingView} onEnter={determineSpecificStructureAccess} />}
      />

      {/* POWER */}
      <Route
        path="readings"
        element={<RequireAuth component={ReadingsRouterView} onEnter={determineSpecificStructureAccess} />}
      />
      <Route
        path="readings/power"
        element={<RequireAuth component={ReadingsRouterView} onEnter={determineSpecificStructureAccess} />}
      />
      <Route
        path="measurements"
        element={<RequireAuth component={ReadingsRouterView} onEnter={determineSpecificStructureAccess} />}
      />
      <Route
        path="readings/power/meter"
        element={<RequireAuth component={InternationalMeterReadingsView} onEnter={determineIsStaff} />}
      />
      <Route
        path="readings/power/consumption"
        element={<RequireAuth component={PowerConsumptionView} onEnter={determineSpecificStructureAccess} />}
      />
      <Route
        path="readings/power/solar"
        element={<RequireAuth component={SolarPowerView} onEnter={determineSpecificStructureAccess} />}
      />
      <Route
        path="readings/power/ansi"
        element={<RequireAuth component={AnsiDoubleConsumptionView} onEnter={determineSpecificStructureAccess} />}
      />

      <Route
        path="solaroptions"
        element={<RequireAuth component={SolarOptionsView} onEnter={determineSpecificStructureAccess} />}
      />
      {/* TICKETS */}
      <Route path="ticket/new" element={<RequireAuth component={NewTicketViewSite} onEnter={determineIsStaff} />} />
      <Route path="tickets" element={<RequireAuth component={SiteTicketGridView} onEnter={determineIsStaff} />} />
    </Route>
    {/* Plant-level compliance stays standalone — distinct from the
        structure-scoped `compliance` child route nested above. */}
    <Route
      path={siteGroupCompliancePath.pattern}
      element={<RequireAuth component={SiteGroupDashboardView} onEnter={determineSpecificPlantAccess} />}
    />
  </>
);
